import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getDevicePlaneDevice, getDevicePlaneLiveStatus } from '../api/devicePlane.js';
import { downloadCsv } from '../lib/csv.js';

const KIND_LABEL: Record<string, string> = {
  stair: 'Stair light',
  ceiling: 'Ceiling light',
  lift: 'Lift',
  unknown: 'Uncategorised',
};

const TICK = { fontSize: 10, fill: '#898781' };
const TOOLTIP_STYLE = {
  background: '#1a1a19',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 12,
};

function ChartEmpty({ msg }: { msg: string }) {
  return <div className="h-36 flex items-center justify-center text-xs text-muted text-center px-4">{msg}</div>;
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-white/10 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-ink2">{title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

export function DevicePlaneDevice() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ['dp-device', deviceId],
    queryFn: () => getDevicePlaneDevice(deviceId!),
    enabled: !!deviceId,
    refetchInterval: 10000,
  });
  const { data: liveStatus } = useQuery({
    queryKey: ['dp-live-status-all'],
    queryFn: () => getDevicePlaneLiveStatus(),
    refetchInterval: 10000,
  });

  if (isLoading || !data) {
    return <div className="p-6 text-sm text-muted">Loading device…</div>;
  }

  const d = data.device;
  const status = liveStatus?.items.find((s) => s.deviceId === d.deviceId) ?? null;

  // Telemetry: one chart per reading, like the demo (no energy).
  const readingSeries = (key: string) =>
    data.telemetry
      .slice()
      .reverse()
      .filter((t) => typeof t.readings[key] === 'number')
      .map((t) => ({
        t: new Date(t.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        v: t.readings[key] as number,
      }));
  const ldrPoints = readingSeries('ldr');
  const brightPoints = readingSeries('brightness');

  // Motion events bucketed per hour from pir readings.
  const pirBuckets = new Map<string, number>();
  for (const t of data.telemetry) {
    if (typeof t.readings['pir'] === 'number' && (t.readings['pir'] as number) > 0) {
      const h = new Date(t.ts).toLocaleTimeString([], { hour: '2-digit' });
      pirBuckets.set(h, (pirBuckets.get(h) ?? 0) + (t.readings['pir'] as number));
    }
  }
  const pirPoints = [...pirBuckets.entries()].reverse().map(([t, v]) => ({ t, v }));

  const uptimePoints = data.presenceHistory
    .slice()
    .reverse()
    .map((p) => ({
      t: new Date(p.ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit' }),
      online: p.online ? 1 : 0,
    }));

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-muted">
            <Link to="/device-plane/fleet" className="hover:underline">Fleet</Link> / device
          </p>
          <h1 className="text-xl font-bold text-ink font-mono">{d.serialNumber}</h1>
          <p className="text-sm mt-0.5 font-mono text-xs text-muted">{d.deviceId}</p>
        </div>
        <div className="flex gap-4 text-sm flex-wrap">
          <span className="inline-block text-xs font-medium rounded-full px-2.5 py-1 bg-accent/15 text-[#a9c9f2] self-center">
            {KIND_LABEL[d.kind] ?? d.kind}
          </span>
          <span className="text-muted self-center">family <b className="text-ink2">{d.family}</b></span>
          <span className="text-muted self-center">lifecycle <b className="text-ink2">{d.lifecycleState}</b></span>
          <span className={`self-center font-semibold ${d.presence === 'online' ? 'text-goodink' : 'text-badink'}`}>
            ● {d.presence}
          </span>
          <span className="text-muted self-center">
            last seen <b className="text-ink2">{d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : '—'}</b>
          </span>
        </div>
      </div>

      {/* Live snapshot — from the persisted status leaf */}
      {status?.statusTs && (
        <Section title={`Live snapshot (reported ${new Date(status.statusTs).toLocaleTimeString()})`}>
          <div className="flex flex-wrap gap-2 text-xs">
            {status.kind === 'lift' ? (
              <>
                <span className="rounded-full px-2.5 py-1 bg-surface2 text-ink2 font-medium">
                  floor {status.currentFloor === null ? '—' : status.currentFloor === 0 ? 'G' : status.currentFloor}
                </span>
                <span className={`rounded-full px-2.5 py-1 font-medium ${status.doorOpen ? 'bg-warn/15 text-warnink' : 'bg-surface2 text-ink2'}`}>
                  door {status.doorOpen ? 'OPEN' : 'closed'}
                </span>
                <span className="rounded-full px-2.5 py-1 bg-surface2 text-ink2 font-medium">fan {status.fan ? 'on' : 'off'}</span>
                <span className="rounded-full px-2.5 py-1 bg-surface2 text-ink2 font-medium">light {status.light ? 'on' : 'off'}</span>
              </>
            ) : (
              <>
                <span className="rounded-full px-2.5 py-1 bg-surface2 text-ink2 font-medium">brightness {status.brightness ?? '—'}%</span>
                <span className="rounded-full px-2.5 py-1 bg-accent/15 text-[#a9c9f2] font-medium">{status.mode ?? 'no mode'}</span>
                <span className="rounded-full px-2.5 py-1 bg-surface2 text-ink2 font-medium">LDR {status.ldrEnabled ? 'on' : 'off'}</span>
              </>
            )}
          </div>
        </Section>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <Section title="Presence (last 100 transitions)">
          {uptimePoints.length > 0 ? (
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={uptimePoints} margin={{ top: 4, right: 8, bottom: 0, left: -28 }}>
                  <XAxis dataKey="t" tick={TICK} interval="preserveStartEnd" stroke="#383835" />
                  <YAxis domain={[0, 1]} ticks={[0, 1]} tick={TICK} stroke="#383835" />
                  <Tooltip formatter={(v) => (v === 1 ? 'online' : 'offline')} contentStyle={TOOLTIP_STYLE} />
                  <Line type="stepAfter" dataKey="online" stroke="#0ca30c" dot={uptimePoints.length < 3} name="online" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ChartEmpty msg="No presence transitions recorded yet." />
          )}
        </Section>

        <Section title="Ambient light (LDR)">
          {ldrPoints.length > 0 ? (
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={ldrPoints} margin={{ top: 4, right: 8, bottom: 0, left: -22 }}>
                  <XAxis dataKey="t" tick={TICK} interval="preserveStartEnd" stroke="#383835" />
                  <YAxis tick={TICK} stroke="#383835" />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="v" stroke="#3987e5" fill="rgba(57,135,229,0.15)"
                    dot={ldrPoints.length < 3} name="ldr" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ChartEmpty msg="No ldr readings yet — appears once the device sends telemetry." />
          )}
        </Section>

        <Section title="Brightness">
          {brightPoints.length > 0 ? (
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={brightPoints} margin={{ top: 4, right: 8, bottom: 0, left: -22 }}>
                  <XAxis dataKey="t" tick={TICK} interval="preserveStartEnd" stroke="#383835" />
                  <YAxis tick={TICK} stroke="#383835" domain={[0, 100]} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="v" stroke="#c98500" fill="rgba(201,133,0,0.15)"
                    dot={brightPoints.length < 3} name="brightness %" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ChartEmpty msg="No brightness readings yet — appears once the device sends telemetry." />
          )}
        </Section>

        <Section title="Motion events (PIR) per hour">
          {pirPoints.length > 0 ? (
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pirPoints} margin={{ top: 4, right: 8, bottom: 0, left: -28 }}>
                  <XAxis dataKey="t" tick={TICK} interval="preserveStartEnd" stroke="#383835" />
                  <YAxis tick={TICK} allowDecimals={false} stroke="#383835" />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="v" fill="#9085e9" radius={[3, 3, 0, 0]} name="motion events" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ChartEmpty msg="No motion (pir) readings yet — appears once the device sends telemetry." />
          )}
        </Section>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Section
          title="Command history (last 50)"
          action={
            <button
              onClick={() => downloadCsv(`commands-${d.serialNumber}`, data.commands)}
              className="text-xs text-accent hover:underline"
            >
              Export CSV
            </button>
          }
        >
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-grid">
                  <th className="py-1.5">Time</th><th className="py-1.5">Type</th>
                  <th className="py-1.5">Outcome</th><th className="py-1.5">Detail</th>
                  <th className="py-1.5">Origin</th><th className="py-1.5 text-right">Latency</th>
                </tr>
              </thead>
              <tbody>
                {data.commands.map((c) => {
                  const detail = (c.detail ?? {}) as Record<string, unknown>;
                  const errorCode = typeof detail['errorCode'] === 'string' ? (detail['errorCode'] as string) : null;
                  const reason = typeof detail['reason'] === 'string' ? (detail['reason'] as string) : null;
                  return (
                    <tr key={`${c.cmdId}-${c.ts}`} className="border-b border-grid">
                      <td className="py-1.5 text-xs text-muted">{new Date(c.ts).toLocaleString()}</td>
                      <td className="py-1.5 font-mono text-xs text-ink2">{c.cmdType}</td>
                      <td className={`py-1.5 text-xs font-medium ${
                        c.outcome === 'ok' ? 'text-goodink' : c.outcome === 'error' ? 'text-badink' : 'text-warnink'
                      }`}>{c.outcome}</td>
                      <td className="py-1.5 text-xs">
                        {errorCode && <span className="font-mono text-badink">{errorCode}</span>}
                        {reason && <span className="text-warnink">{reason}</span>}
                        {!errorCode && !reason && <span className="text-muted">—</span>}
                      </td>
                      <td className="py-1.5 text-xs text-muted">
                        {c.origin ?? '—'}
                        {c.actorId && (
                          <span className="font-mono" title={c.actorId}> · {c.actorId.slice(0, 8)}…</span>
                        )}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-xs text-ink2">{c.latencyMs === null ? '—' : `${c.latencyMs} ms`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Connection events (last 50)">
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-grid">
                  <th className="py-1.5">Time</th><th className="py-1.5">Event</th><th className="py-1.5">Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.connections.map((c, i) => (
                  <tr key={`${c.ts}-${i}`} className="border-b border-grid">
                    <td className="py-1.5 text-xs text-muted">{new Date(c.ts).toLocaleString()}</td>
                    <td className={`py-1.5 text-xs font-medium ${
                      c.event === 'connect' ? 'text-goodink' : c.event === 'disconnect' ? 'text-badink' : 'text-[#a9c9f2]'
                    }`}>{c.event}</td>
                    <td className="py-1.5 text-xs text-muted font-mono">
                      {c.detail ? Object.entries(c.detail).map(([k, v]) => `${k}: ${String(v)}`).join(' · ') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}
